import { vars } from '@etg/wings';
import { useDirection, useRevolut, useProperty, useSiteContext } from '@eti/providers';
// oxlint-disable-next-line no-restricted-imports -- TODO WEBTECH-1501: Replace old schema object types with operation-specific types.
import type { BestPricePerDay } from '@eti/schema-types/schema-objects';
import { css, cx } from '@eti/styles';
import {
  isFirstDayOfMonth,
  isLastDayOfMonth,
  isSameDay,
  startOfMonth,
  type Locale,
} from 'date-fns';
import { type MouseEvent, useEffect, useState } from 'react';
import { type DayEventHandler, DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import { useFormContext } from 'react-hook-form';
import type { FormBound } from '../../../../common/search-form-panel/SearchFormContainer';
import { tripTypes } from '../../../../constants/tripTypesConstants';
import {
  type DEPARTURE_DATE,
  MULTI_BOUND,
  RETURN_DATE,
  SINGLE_BOUND,
} from '../../constants/formFields';
import {
  calculateDayRange,
  getOneYearFromToday,
  isDisabled,
  isMasked,
  isSelected,
} from '../../utils/calendarDateUtils';
import {
  formatWeekdayName,
  formatWeekdayNameSingleLetter,
  getLocale,
} from '../../utils/calendarLocales';
import { getFirstAndLastSelectableDateButtons } from '../../utils/getFirstAndLastSelectableDateButtons';
import CalendarFooter from './CalendarFooter';
import { calendarStyles } from './calendarStyles';
import ChangeMonthButton from './ChangeMonthButton';
import CustomDayWithPriceIndicator from './CustomDayWithPriceIndicator';
import CustomDayWithoutPriceIndicator from './CustomDayWithoutPriceIndicator';

const calendarContainerStyle = css`
  background-color: ${vars.colors.surface.primary};
  border: 1px solid ${vars.colors.inputs.border};
  border-radius: ${vars.inputs.borderRadius};
  box-shadow:
    0 10px 20px rgb(0 0 0 / 4%),
    0 2px 6px rgb(0 0 0 / 4%),
    0 0 1px rgb(0 0 0 / 4%);
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.5rem 0.25rem 1rem;
`;

const noBorderStyles = css`
  border: 0;
`;

const f1CalendarContainerStyle = css`
  gap: 1.5rem;
  padding: 0.5rem 0.5rem 1rem;
`;

export interface CalendarProps {
  bestPricesPerDay?: BestPricePerDay[];
  boundIndex: number;
  range?: { departureDate?: Date; returnDate?: Date };
  currentField?: typeof DEPARTURE_DATE | typeof RETURN_DATE | null;
  languageCode?: string;
  loading?: boolean;
  daysFromTodayForValidDepartureDate?: number;
  id?: string;
  maxSelectableDate?: Date;
  minSelectableDate?: Date;
  previousBoundDepartureDate?: Date;
  selectedTripType?: string;
  shouldAutoFocusDay?: boolean;
  onDepartureNewDateChange?: (day: Date) => void;
  onReturnNewDateChange?: (day: Date) => void;
  onDepartureDateChange?: (day: string) => void;
  onReturnDateChange?: (day: string) => void;
}

const Calendar = ({
  bestPricesPerDay,
  boundIndex,
  range = {},
  currentField,
  daysFromTodayForValidDepartureDate,
  id,
  loading = false,
  maxSelectableDate,
  minSelectableDate,
  previousBoundDepartureDate,
  selectedTripType,
  shouldAutoFocusDay = false,
  onDepartureDateChange,
  onReturnDateChange,
  onDepartureNewDateChange,
  onReturnNewDateChange,
}: CalendarProps) => {
  const { direction } = useDirection();
  const { getValues, trigger: validateForm } = useFormContext();
  const { language } = useSiteContext();
  const { p } = useProperty();
  const { isRevolut } = useRevolut();

  const isPriceCalendarEnabled = p('IbeClient.Price.Calendar.Enabled');
  const shouldDisplaySingleLetterDay = p('SearchForm.Calendar.SingleLetterWeekday.Enabled');
  const [locale, setLocale] = useState<Locale>();

  const isValidDate = (value: unknown): value is Date => {
    return value instanceof Date && !Number.isNaN(value.getTime());
  };

  const isReturnTrip = selectedTripType === tripTypes.RETURN;

  const getDefaultMonth = () => {
    const [singleBound, multiBound]: [FormBound, FormBound[]] = getValues([
      SINGLE_BOUND,
      MULTI_BOUND,
    ]);

    if (selectedTripType === tripTypes.MULTI_STOP) {
      const departureDate = multiBound.at(boundIndex)?.departureDate;
      if (isValidDate(departureDate)) {
        return departureDate;
      }

      const lastValidDate = multiBound
        .filter((formBound) => isValidDate(formBound.departureDate))
        .at(-1)?.departureDate;

      return new Date(lastValidDate ?? new Date());
    }

    if (!currentField) {
      return new Date();
    }

    const fieldValue = singleBound[currentField];

    if (isValidDate(fieldValue)) {
      return fieldValue;
    }

    if (
      currentField === RETURN_DATE &&
      !isValidDate(singleBound.returnDate) &&
      isValidDate(singleBound.departureDate)
    ) {
      return singleBound.departureDate;
    }

    return new Date();
  };

  useEffect(() => {
    const loadLocal = async () => {
      if (language?.locale) {
        const tempLocale = await getLocale(language.locale);
        setLocale(tempLocale);
      }
    };
    loadLocal();
  }, [language?.locale]);

  useEffect(() => {
    if (!currentField || !shouldAutoFocusDay) {
      return;
    }

    const dateButtons = getFirstAndLastSelectableDateButtons(range);

    dateButtons[currentField]?.focus();
  }, [currentField, range, shouldAutoFocusDay]);

  const handleDayClick: DayEventHandler<MouseEvent> = (day, { disabled }) => {
    if (!disabled) {
      validateForm();
      calculateDayRange(
        currentField,
        range.departureDate,
        range.returnDate,
        day,
        onDepartureDateChange || onDepartureNewDateChange,
        onReturnDateChange || onReturnNewDateChange,
      );
    }
  };

  const departureDate = range?.departureDate;
  const returnDate = range?.returnDate;
  const isSameDayReturnTrip =
    departureDate && returnDate ? isSameDay(departureDate, returnDate) : false;
  const isOnlyDepartureSelected = !returnDate;
  const shouldShowCustomNonPriceCalendar = isRevolut && !isPriceCalendarEnabled;

  return (
    <div
      className={cx(calendarContainerStyle, {
        [f1CalendarContainerStyle]: isRevolut,
        [noBorderStyles]: isRevolut,
      })}
      data-testid={id}
      id={id}
    >
      <DayPicker
        classNames={calendarStyles(
          isRevolut,
          isReturnTrip,
          isSameDayReturnTrip,
          isOnlyDepartureSelected,
          shouldShowCustomNonPriceCalendar,
        )}
        components={{
          NextMonthButton: (props) => <ChangeMonthButton {...props} direction="next" />,
          PreviousMonthButton: (props) => <ChangeMonthButton {...props} direction="previous" />,
          ...(isPriceCalendarEnabled || shouldShowCustomNonPriceCalendar
            ? {
                DayButton: ({ day, modifiers, ...buttonProps }) => (
                  <>
                    {shouldShowCustomNonPriceCalendar ? (
                      <CustomDayWithoutPriceIndicator
                        date={day.date}
                        modifiers={modifiers}
                        {...buttonProps}
                      />
                    ) : (
                      <CustomDayWithPriceIndicator
                        date={day.date}
                        loading={loading}
                        modifiers={modifiers}
                        priceCalendarData={bestPricesPerDay ?? undefined}
                        {...buttonProps}
                      />
                    )}
                  </>
                ),
              }
            : {}),
        }}
        defaultMonth={getDefaultMonth()}
        dir={direction}
        endMonth={maxSelectableDate ?? getOneYearFromToday()}
        formatters={{
          formatWeekdayName: shouldDisplaySingleLetterDay
            ? formatWeekdayNameSingleLetter
            : formatWeekdayName,
        }}
        locale={locale}
        modifiers={{
          selected: (day) => isSelected(day, range.departureDate, range.returnDate),
          disabled: (day) =>
            isDisabled(
              day,
              daysFromTodayForValidDepartureDate,
              previousBoundDepartureDate,
              selectedTripType,
              minSelectableDate,
              maxSelectableDate,
            ),
          masked: (day) => isMasked(day, selectedTripType, range.departureDate, range.returnDate),
          range_start: isReturnTrip ? range.departureDate : undefined,
          range_end: isReturnTrip ? range.returnDate : undefined,
          isFirstDayOfMonth: (day) => isFirstDayOfMonth(day),
          isLastDayOfMonth: (day) => isLastDayOfMonth(day),
        }}
        modifiersClassNames={{
          masked: 'rdp-masked',
          isFirstDayOfMonth: 'rdp-is-first-day-of-month',
          isLastDayOfMonth: 'rdp-is-last-day-of-month',
        }}
        modifiersStyles={{
          disabled: { opacity: 1 },
          today: { color: vars.colors.text.primary },
        }}
        onDayClick={handleDayClick}
        startMonth={startOfMonth(new Date())}
      />
      {selectedTripType === tripTypes.RETURN && (range.departureDate || range.returnDate) && (
        <CalendarFooter {...range} />
      )}
    </div>
  );
};

export default Calendar;
